import { QueryClient } from '@tanstack/react-query'

// Create a client with proper defaults for the food ordering system
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time of 5 minutes for most data
      staleTime: 5 * 60 * 1000,
      // Cache time of 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests up to 3 times
      retry: 3,
      // Retry delay that increases exponentially
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Disable refetch on window focus for better performance
      // Users can manually refresh if needed
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default (can be overridden per query)
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      // Shorter retry delay for mutations
      retryDelay: 1000,
    },
  },
})

// Query key factories for consistent cache management
export const queryKeys = {
  // Restaurant queries
  restaurants: {
    all: ['restaurants'] as const,
    lists: () => [...queryKeys.restaurants.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.restaurants.lists(), filters] as const,
    details: () => [...queryKeys.restaurants.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.restaurants.details(), id] as const,
  },

  // Menu queries
  menu: {
    all: ['menu'] as const,
    items: (restaurantId: string) => [...queryKeys.menu.all, 'items', restaurantId] as const,
    item: (itemId: string) => [...queryKeys.menu.all, 'item', itemId] as const,
  },

  // Customization queries
  customizations: {
    all: ['menu-customizations'] as const,
    item: (menuItemId: string) => [...queryKeys.customizations.all, menuItemId] as const,
  },

  // Order queries
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
  },

  // Zone queries
  zones: {
    all: ['zones'] as const,
    list: () => [...queryKeys.zones.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.zones.all, id] as const,
  },
}
